import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Badge from '../UI/Badge';

export default function QuizTake({ userRole }) {
  const { courseId, quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  useEffect(() => {
    if (quiz?.timeLimit && !submission && userRole === 'STUDENT') {
      setTimeLeft(quiz.timeLimit * 60); // Convert to seconds

      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmit(); // Auto-submit when time runs out
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [quiz, submission]);

  const fetchQuiz = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/quizzes/${quizId}`);
      setQuiz(response.data.quiz);
      setAnswers(new Array(response.data.quiz.questions?.length || 0).fill(null));

      if (userRole === 'STUDENT') {
        // Check if already submitted
        try {
          const subResponse = await api.get(`/api/quizzes/${quizId}/my-submission`);
          setSubmission(subResponse.data.submission);
        } catch (err) {
          // No submission yet, that's ok
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || '퀴즈를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionIndex, answerIndex) => {
    const updated = [...answers];
    updated[questionIndex] = answerIndex;
    setAnswers(updated);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const response = await api.post(`/api/quizzes/${quizId}/submit`, { answers });

      if (response.data.success) {
        setSubmission(response.data.submission);
      }
    } catch (err) {
      setError(err.response?.data?.error || '퀴즈 제출에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="text-center py-8">
        <p className="text-danger-500 mb-4">{error}</p>
        <Button onClick={() => navigate(`/courses/${courseId}`)}>수업으로 돌아가기</Button>
      </Card>
    );
  }

  if (!quiz) {
    return (
      <Card className="text-center py-8">
        <p className="text-neutral-500 mb-4">퀴즈를 찾을 수 없습니다.</p>
        <Button onClick={() => navigate(`/courses/${courseId}`)}>수업으로 돌아가기</Button>
      </Card>
    );
  }

  // Show results if already submitted
  if (submission) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <h1 className="text-3xl font-bold text-neutral-800 mb-4">{quiz.title}</h1>
          <div className="p-6 bg-secondary-50 rounded-xl">
            <p className="text-2xl font-bold text-secondary-700 mb-2">
              점수: {submission.score}점
            </p>
            <p className="text-neutral-600">
              {quiz.questions.length}문제 중 {Math.round((submission.score / 100) * quiz.questions.length)}문제 정답
            </p>
          </div>
        </Card>

        {/* Show correct answers */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-neutral-800">정답 확인</h2>
          {quiz.questions.map((q, qIndex) => {
            const userAnswer = submission.answers[qIndex];
            const isCorrect = userAnswer === q.correctAnswer;

            return (
              <Card key={qIndex} className={isCorrect ? 'border-2 border-secondary-200' : 'border-2 border-danger-200'}>
                <div className="flex items-start gap-3 mb-3">
                  <Badge variant={isCorrect ? 'success' : 'danger'}>
                    {isCorrect ? '정답' : '오답'}
                  </Badge>
                  <h3 className="font-medium text-neutral-800 flex-1">
                    {qIndex + 1}. {q.question}
                  </h3>
                </div>

                <div className="space-y-2">
                  {q.options.map((option, oIndex) => (
                    <div
                      key={oIndex}
                      className={`p-3 rounded-lg ${
                        oIndex === q.correctAnswer
                          ? 'bg-secondary-50 border-2 border-secondary-200'
                          : oIndex === userAnswer && !isCorrect
                          ? 'bg-danger-50 border-2 border-danger-200'
                          : 'bg-neutral-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {oIndex === userAnswer && (
                          <span className="text-sm">✓</span>
                        )}
                        <span>{option}</span>
                        {oIndex === q.correctAnswer && (
                          <Badge variant="success" size="sm">정답</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>

        <div className="text-center">
          <Button onClick={() => navigate(`/courses/${courseId}`)}>
            수업으로 돌아가기
          </Button>
        </div>
      </div>
    );
  }

  // Quiz taking interface (for students)
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Card>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-neutral-800 mb-2">{quiz.title}</h1>
            {quiz.description && (
              <p className="text-neutral-600">{quiz.description}</p>
            )}
          </div>
          {timeLeft !== null && (
            <Badge variant={timeLeft < 60 ? 'danger' : 'primary'} size="lg">
              {formatTime(timeLeft)}
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-neutral-100">
          <div>
            <p className="text-sm text-neutral-500">문제 수</p>
            <p className="font-medium text-neutral-800">{quiz.questions?.length || 0}문제</p>
          </div>
          {quiz.timeLimit && (
            <div>
              <p className="text-sm text-neutral-500">제한 시간</p>
              <p className="font-medium text-neutral-800">{quiz.timeLimit}분</p>
            </div>
          )}
        </div>
      </Card>

      {/* Questions */}
      <div className="space-y-6">
        {quiz.questions?.map((q, qIndex) => (
          <Card key={qIndex}>
            <h3 className="font-bold text-neutral-800 mb-4">
              {qIndex + 1}. {q.question}
            </h3>

            <div className="space-y-2">
              {q.options.map((option, oIndex) => (
                <label
                  key={oIndex}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    answers[qIndex] === oIndex
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question_${qIndex}`}
                    checked={answers[qIndex] === oIndex}
                    onChange={() => handleAnswerChange(qIndex, oIndex)}
                    className="w-5 h-5 text-primary-600"
                  />
                  <span className="flex-1">{option}</span>
                </label>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Submit Button */}
      <Card className="sticky bottom-4">
        <div className="flex items-center justify-between">
          <p className="text-neutral-600">
            {answers.filter(a => a !== null).length} / {quiz.questions?.length || 0} 문제 선택됨
          </p>
          <Button
            onClick={handleSubmit}
            disabled={submitting || answers.some(a => a === null)}
          >
            {submitting ? '제출 중...' : '퀴즈 제출'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
